import { zodResolver } from "@hookform/resolvers/zod";
import { Time } from "@internationalized/date";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  Button,
  DatePicker,
  TimePicker,
} from "@mozioinc/ui";
import { useNavigate } from "@tanstack/react-router";
import { addDays, subDays } from "date-fns";
import { useState, type ReactNode } from "react";
import { useForm, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LocationCombobox } from "./location-combobox";
import type { TransNamespace } from "../types";
import type { Location } from "@mozioinc/query";
import type { TimePickerProps } from "@mozioinc/ui";

export const locationSchema = z.object({
  displayText: z.string(),
  name: z.string(),
  value: z.string(),
  lat: z.number(),
  lng: z.number(),
});

const addTimeToDate = (date: Date, time: Time) => {
  const newDate = new Date(date);
  newDate.setHours(time.hour);
  newDate.setMinutes(time.minute);
  newDate.setSeconds(time.second);
  newDate.setMilliseconds(time.millisecond);
  newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset());
  return newDate;
};

const formSchema = z
  .object({
    location: locationSchema,
    departureDate: z.date().min(subDays(new Date(), 1)).optional(),
    departureTime: z.instanceof(Time).optional(),
    arrivalDate: z.date().min(subDays(new Date(), 1)).optional(),
    arrivalTime: z.instanceof(Time).optional(),
  })
  .refine(
    (data) => {
      if (
        data.departureDate &&
        data.departureTime &&
        data.arrivalDate &&
        data.arrivalTime
      ) {
        const departureDate = addTimeToDate(
          data.departureDate,
          data.departureTime,
        );
        const arrivalDate = addTimeToDate(data.arrivalDate, data.arrivalTime);
        if (arrivalDate > departureDate) {
          return false;
        }
      }

      return true;
    },
    {
      path: ["departureDate"],
    },
  );

const getDefaultDate = (direction: "arrival" | "departure", date?: string) => {
  const daysToAdd = direction === "arrival" ? 1 : 7;
  return date ? new Date(date) : addDays(new Date(), daysToAdd);
};

const getDefaultTime = (date?: string) =>
  date
    ? new Time(new Date(date).getHours(), new Date(date).getMinutes())
    : new Time(12);

type SearchFormProps = {
  onSubmit?: () => void;
  defaultArrivalDateTime?: string;
  defaultDepartureDateTime?: string;
  defaultLocation?: Location;
  onLocationChange?: (value: Location | null) => void;
  onDateTimeChange?: (name: string, value: string) => void;
};

const SearchForm = ({
  onSubmit,
  defaultArrivalDateTime,
  defaultDepartureDateTime,
  defaultLocation,
  onLocationChange,
  onDateTimeChange,
}: SearchFormProps) => {
  const [openArrivalDate, setOpenArrivalDate] = useState(false);
  const [openDepartureDate, setOpenDepartureDate] = useState(false);
  const [openArrivalTime, setOpenArrivalTime] = useState(false);
  const [openDepartureTime, setOpenDepartureTime] = useState(false);

  const { t } = useTranslation<TransNamespace>("parking:search-form");
  const navigate = useNavigate({ from: "/" });

  type FormValues = z.infer<typeof formSchema>;

  const { handleSubmit, control } = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  const locationField = useController({
    name: "location",
    control,
    defaultValue: defaultLocation,
  });
  const arrivalDateField = useController({
    name: "arrivalDate",
    control,
    defaultValue: getDefaultDate("arrival", defaultArrivalDateTime),
  });
  const arrivalTimeField = useController({
    name: "arrivalTime",
    control,
    defaultValue: getDefaultTime(defaultArrivalDateTime),
  });
  const departureDateField = useController({
    name: "departureDate",
    control,
    defaultValue: getDefaultDate("departure", defaultDepartureDateTime),
  });
  const departureTimeField = useController({
    name: "departureTime",
    control,
    defaultValue: getDefaultTime(defaultDepartureDateTime),
  });

  const submit = ({
    location,
    arrivalDate,
    arrivalTime,
    departureDate,
    departureTime,
  }: FormValues) => {
    if (!departureDate) {
      (
        document.querySelector(
          "button[name=departureDate], input[name=departureDate]",
        ) as HTMLElement
      )?.click();
      return;
    }
    if (!departureTime) {
      (
        document.querySelector(
          "button[name=departureTime], input[name=departureTime]",
        ) as HTMLElement
      )?.click();
      return;
    }
    if (!arrivalDate) {
      (
        document.querySelector(
          "button[name=arrivalDate], input[name=arrivalDate]",
        ) as HTMLElement
      )?.click();
      return;
    }
    if (!arrivalTime) {
      (
        document.querySelector(
          "button[name=arrivalTime], input[name=arrivalTime]",
        ) as HTMLElement
      )?.click();
      return;
    }

    departureDate = addTimeToDate(departureDate, departureTime);
    arrivalDate = addTimeToDate(arrivalDate, arrivalTime);

    navigate({
      to: "/search",
      search: (prev) => ({
        ...prev,
        location,
        arrivalDateTime: `${arrivalDate?.toISOString().slice(0, -1)}`,
        departureDateTime: `${departureDate?.toISOString().slice(0, -1)}`,
      }),
    });
    onSubmit?.();
  };

  const handleDateTimeChange = (name: string, date: Date, time: Time) => {
    const value = addTimeToDate(date, time);

    onDateTimeChange?.(name, value.toISOString().slice(0, -1));
  };

  const handleArrivalDateChange = (value?: Date) => {
    arrivalDateField.field.onChange(value);
    const arrivalTime = arrivalTimeField.field.value;

    if (value && arrivalTime) {
      handleDateTimeChange("arrivalDateTime", value, arrivalTime);
    }

    const isUserDefinedDepartureDate =
      departureDateField.fieldState.isDirty || !!defaultDepartureDateTime;

    if (!isUserDefinedDepartureDate && value) {
      const sevenDaysLater = addDays(value, 7);
      departureDateField.field.onChange(sevenDaysLater);
    }

    setOpenArrivalTime(true);
  };

  const handleArrivalTimeChange = (value: TimePickerProps["value"]) => {
    arrivalTimeField.field.onChange(value);
    const arrivalDate = arrivalDateField.field.value;

    if (arrivalDate) {
      handleDateTimeChange("arrivalDateTime", arrivalDate, value as Time);
    }
  };

  const handleDepartureDateChange = (value: Date | undefined) => {
    departureDateField.field.onChange(value);

    const departureTime = departureTimeField.field.value;

    if (value && departureTime) {
      handleDateTimeChange("departureDateTime", value, departureTime);
    }

    setOpenDepartureTime(true);
  };

  const handleDepartureTimeChange = (value: TimePickerProps["value"]) => {
    departureTimeField.field.onChange(value);
    const departureDate = departureDateField.field.value;

    if (departureDate) {
      handleDateTimeChange("departureDateTime", departureDate, value as Time);
    }
  };

  const handleLocationValueChange = (value: Location | null) => {
    locationField.field.onChange(value);
    onLocationChange?.(value);
  };

  return (
    <form
      className="flex flex-col gap-2 lg:gap-5"
      onSubmit={handleSubmit(submit)}
    >
      <div className="flex flex-col space-y-2 lg:flex-row lg:space-x-5 lg:space-y-0 [&>div]:flex-1">
        <div className="relative">
          <LocationCombobox
            id="location"
            label={t("Parking At")}
            name={locationField.field.name}
            error={Boolean(locationField.fieldState.error)}
            onBlur={locationField.field.onBlur}
            onValueChange={handleLocationValueChange}
            defaultValue={locationField.field.value?.displayText}
          />
          {locationField.fieldState.error && (
            <ErrorMessage>
              {t("Please enter a location to search for parking.")}
            </ErrorMessage>
          )}
        </div>

        <div className="relative">
          <div className="grid grid-cols-2 [&>div]:!border-neutral-500 first:[&>div]:rounded-r-none first:[&>div]:border-r-0 last:[&>div]:rounded-l-none last:[&>div]:border-l-0">
            <DatePicker
              id="arrivalDate"
              label={t("Drop Off Date")}
              name={arrivalDateField.field.name}
              value={arrivalDateField.field.value}
              fromDate={new Date()}
              error={Boolean(arrivalDateField.fieldState.error)}
              onValueChange={handleArrivalDateChange}
              open={openArrivalDate}
              onOpenChange={setOpenArrivalDate}
            />

            <TimePicker
              id="arrivalTime"
              label={t("Drop Off Time")}
              name={arrivalTimeField.field.name}
              value={arrivalTimeField.field.value}
              error={Boolean(arrivalTimeField.fieldState.error)}
              onValueChange={handleArrivalTimeChange}
              onDone={() => setOpenDepartureDate(true)}
              open={openArrivalTime}
              onOpenChange={setOpenArrivalTime}
            />
          </div>
          {arrivalDateField.fieldState.error && (
            <ErrorMessage>
              {t("Drop off date must be after today")}
            </ErrorMessage>
          )}
        </div>

        <div className="relative">
          <div className="grid grid-cols-2 [&>div]:!border-neutral-500 first:[&>div]:rounded-r-none first:[&>div]:border-r-0 last:[&>div]:rounded-l-none last:[&>div]:border-l-0">
            <DatePicker
              id="departureDate"
              label={t("Pick Up Date")}
              name={departureDateField.field.name}
              value={departureDateField.field.value}
              error={Boolean(departureDateField.fieldState.error)}
              fromDate={arrivalDateField.field.value ?? new Date()}
              onValueChange={handleDepartureDateChange}
              open={openDepartureDate}
              onOpenChange={setOpenDepartureDate}
            />

            <TimePicker
              id="departureTime"
              label={t("Pick Up Time")}
              name={departureTimeField.field.name}
              value={departureTimeField.field.value}
              error={Boolean(departureTimeField.fieldState.error)}
              onValueChange={handleDepartureTimeChange}
              open={openDepartureTime}
              onOpenChange={setOpenDepartureTime}
            />
          </div>
          {departureDateField.fieldState.error && (
            <ErrorMessage>
              {departureDateField.fieldState.error.type === "custom"
                ? t("Pick up date must be after drop off date")
                : t("Pick up date must be after today")}
            </ErrorMessage>
          )}
        </div>
      </div>
      <Button
        type="submit"
        size={BUTTON_SIZE.LG}
        color={BUTTON_COLOR.SECONDARY}
      >
        {t("Search Car Parks")}
      </Button>
    </form>
  );
};

const ErrorMessage = ({ children }: { children: ReactNode }) => (
  <p className="text-error text-sm lg:absolute">{children}</p>
);

export { SearchForm };
